import { catchError, interval, map, of, switchMap, withLatestFrom } from 'rxjs';

import { Injectable } from '@angular/core';
import { NotificaApiClient } from '@ideapa/app-api-client';
import { NgrxLoaderFacade } from '@ideapa/app-ngrx-loader';
import { NotificaInApp } from '@ideapa/app-shared-notifica';
import { NotificaListWithCounter } from '@ideapa/common-dto';
import { ToastService } from '@ideapa/app-ui-kit';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as NotificationsActions from './notifications.actions';
import { NotificationsFacade } from './notifications.facade';

const POLL_INTERVAL_MS = 30000;

@Injectable()
export class NotificationsEffects {
  constructor(
    private actions$: Actions,
    private facade: NotificationsFacade,
    private loader: NgrxLoaderFacade,
    private apiClient: NotificaApiClient,
    private toastService: ToastService
  ) {}

  getUserUnreadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.getUserLatestNotifications),
      switchMap((action) => {
        this.loader.showLoader(action);
        return this.apiClient.getUserLatestNotifications().pipe(
          map((response) => {
            this.loader.hideLoader(action);

            return NotificationsActions.getUserLatestNotificationsSuccess({
              items: this.mapToNotificheInApp(response),
              counter: response.unreadCount,
            });
          }),
          catchError((error) => {
            this.loader.hideLoader(action);
            return of(
              NotificationsActions.getUserLatestNotificationsFailure(error)
            );
          })
        );
      })
    )
  );

  // Interroga periodicamente il backend invece di mantenere una connessione
  // persistente (SSE): evita di occupare permanentemente uno slot di
  // connessione HTTP/1.1 condiviso con il caricamento dei chunk lazy dell'app.
  subscribeUserNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.subscribeUserNotifications),
      switchMap(() => interval(POLL_INTERVAL_MS)),
      withLatestFrom(this.facade.items$),
      switchMap(([, currentItems]) => {
        const knownIds = new Set(currentItems.map((item) => item.id));

        return this.apiClient.getUserLatestNotifications().pipe(
          map((response) => {
            response.items
              .filter((dto) => !knownIds.has(dto.id))
              .forEach((dto) => {
                const contenutoNotifica = JSON.parse(dto.contenuto);
                this.toastService.successFeedback(
                  contenutoNotifica.titolo,
                  contenutoNotifica.testo
                );
              });

            return NotificationsActions.getUserLatestNotificationsSuccess({
              items: this.mapToNotificheInApp(response),
              counter: response.unreadCount,
            });
          }),
          catchError((error) =>
            of(NotificationsActions.subscribeUserNotificationsFailure(error))
          )
        );
      })
    )
  );

  private mapToNotificheInApp(
    response: NotificaListWithCounter
  ): NotificaInApp[] {
    return response.items.map((notificaDto) => {
      const contenutoNotifica = JSON.parse(notificaDto.contenuto);

      return {
        id: notificaDto.id,
        timestamp: notificaDto.timestamp,
        stato: notificaDto.stato,
        titolo: contenutoNotifica.titolo,
        testo: contenutoNotifica.testo,
      };
    });
  }
}
